import React, { useMemo, useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import MUIDataTable from "mui-datatables";
import Swal from "sweetalert2";
import { getProductVariants, updateVariantQuantity } from "../Redux/Reducers/ProductSlice";
import DefaultAvatar from "../otherImages/default.png";
import { getPaginationTotal, getRowsPerPageOptions } from "../helper/tablePagination";
import useTableUrlState from "../hook/useTableUrlState";
import { getSortOrder, sortTableData } from "../helper/tableSort";

const InnerInventoryList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [localStock, setLocalStock] = useState({});
  const [updatingVariant, setUpdatingVariant] = useState(null);
  const [suitInventoryTab, setSuitInventoryTab] = useState("coat");
  const [{ page, rowsPerPage, sortName, sortDirection }, setTableState] = useTableUrlState({
    page: 0,
    rowsPerPage: 12,
    sortName: "",
    sortDirection: "",
  });
  
  const { variants = [], loading, error, updatingQuantity, variantsPagination } = useSelector((state) => state.products || { variants: [], loading: false, error: null, updatingQuantity: false, variantsPagination: null });

  useEffect(() => {
    dispatch(getProductVariants({ productId: id, page: page + 1, perPage: rowsPerPage }));
  }, [dispatch, id, page, rowsPerPage]);

  useEffect(() => {
    const stockMap = {};
    variants.forEach(item => {
      stockMap[item.id] = item.stock_quantity || 0;
    });
    const isSame = Object.keys(stockMap).length === Object.keys(localStock).length &&
      Object.keys(stockMap).every(key => localStock[key] === stockMap[key]);
    if (!isSame) {
      setLocalStock(stockMap);
    }
  }, [variants]);

  const handleQuantityUpdate = async (variantId, newQuantity) => {
    if (updatingVariant === variantId) return;
    
    setUpdatingVariant(variantId);
    try {
      const response = await dispatch(updateVariantQuantity({ 
        variantId, 
        quantity: newQuantity 
      })).unwrap();
      
      if (response?.success) {

        setLocalStock(prev => ({
          ...prev,
          [variantId]: newQuantity
        }));
      } else {
        // Revert to original value on error
        const originalVariant = variants.find(v => v.id === variantId);
        setLocalStock(prev => ({
          ...prev,
          [variantId]: originalVariant?.stock_quantity || 0
        }));
        
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: response?.message || "Failed to update quantity.",
        });
      }
    } catch (error) {
      // Revert to original value on error
      const originalVariant = variants.find(v => v.id === variantId);
      setLocalStock(prev => ({
        ...prev,
        [variantId]: originalVariant?.stock_quantity || 0
      }));
      
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: error || "Something went wrong while updating quantity.",
      });
    } finally {
      setUpdatingVariant(null);
    }
  };

  const handleStockChange = (variantId, newValue) => {
    const quantity = Math.max(0, parseInt(newValue) || 0);
    setLocalStock(prev => ({
      ...prev,
      [variantId]: quantity
    }));
    
    // Debounce the API call
    const timeoutId = setTimeout(() => {
      handleQuantityUpdate(variantId, quantity);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  const isSuitProduct = useMemo(
    () =>
      variants.some((variant) => {
        const categoryName =
          variant?.product?.category?.name ||
          variant?.product?.category_name ||
          variant?.category?.name ||
          "";
        return (
          categoryName === "Suits" ||
          variant?.size_category === "coat" ||
          variant?.size_category === "pants"
        );
      }),
    [variants],
  );

  const tableVariants = useMemo(
    () =>
      isSuitProduct
        ? variants.filter((variant) => variant?.size_category === suitInventoryTab)
        : variants,
    [isSuitProduct, suitInventoryTab, variants],
  );

  const sortedVariants = useMemo(
    () =>
      sortTableData(tableVariants, sortName, sortDirection, {
        product_name: (variant) => variant?.product_name || variant?.product?.name || "",
        stock_quantity: (variant) => variant?.stock_quantity || 0,
      }),
    [tableVariants, sortName, sortDirection],
  );

  const commonColumns = [
    {
      name: "product_name",
      label: "Product Name",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = sortedVariants[tableMeta.rowIndex];
          const productImages = row?.product?.images || [];
          const variantColor = (row?.color || row?.color_name || "").toLowerCase();
          const colorMatchedImage = productImages.find((image) => {
            const imageColor = (image?.color || image?.color_name || "").toLowerCase();
            return variantColor && imageColor === variantColor;
          });
          const primaryImage = productImages.find((image) =>
            image?.is_primary === true ||
            image?.is_primary === 1 ||
            image?.is_primary === "1" ||
            image?.is_primray === true ||
            image?.is_primray === 1 ||
            image?.is_primray === "1"
          );
          const productImage = primaryImage?.image_url ||
                          primaryImage?.url ||
                          colorMatchedImage?.image_url ||
                          colorMatchedImage?.url ||
                          productImages[0]?.image_url ||
                          productImages[0]?.url ||
                          row?.product?.image_url || 
                          DefaultAvatar;
          return (
            <div className="d-flex align-items-center gap-8">
              <img
                style={{ height: "35px", width: "35px", borderRadius: "50%", objectFit: "cover" }}
                src={productImage}
              />
              <span>{value || row?.product?.name || "-"}</span>
            </div>
          );
        },
      },
    },
    {
      name: "buy_type",
      label: "Buy Type",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = sortedVariants[tableMeta.rowIndex];
          const buyType = row?.buy_type || value || "-";
          return (
            <div className="d-flex align-items-center justify-content-center gap-8">
              <span className={`badge col-packageType`}>
                {buyType === "buy" ? "Buy" : buyType === "rent" ? "Rent" : buyType}
              </span>
            </div>
          );
        },
      },
    },
    {
      name: "color",
      label: "Color",
      options: {
        customBodyRender: (value, tableMeta) => {
          const row = sortedVariants[tableMeta.rowIndex];
          return (
            <div className="d-flex align-items-center justify-content-center gap-8">
              {row?.color || row?.color_name || value || "-"}
            </div>
          );
        },
      },
    },
  ];

  const coatColumns = [
    {
      name: "size_type",
      label: "Coat Type",
      options: {
        customBodyRender: (value) => {
          return (
            <div className="d-flex align-items-center justify-content-center gap-8">
              {value || "-"}
            </div>
          );
        },
      },
    },
    {
      name: "size_measurement",
      label: "Coat Measurement",
      options: {
        customBodyRender: (value) => {
          return (
            <div className="d-flex align-items-center justify-content-center gap-8">
              {value || "-"}
            </div>
          );
        },
      },
    },
  ];

  const pantsColumns = [
    {
      name: "waist_measurement",
      label: "Waist Measurement",
      options: {
        customBodyRender: (value) => {
          return (
            <div className="d-flex align-items-center justify-content-center gap-8">
              {value || "-"}
            </div>
          );
        },
      },
    },
    {
      name: "outseam_measurement",
      label: "Outseam Measurement",
      options: {
        customBodyRender: (value) => {
          return (
            <div className="d-flex align-items-center justify-content-center gap-8">
              {value || "-"}
            </div>
          );
        },
      },
    },
  ];

  const stockColumn = {
    name: "stock_quantity",
    label: "Stock Quantity",
    options: {
      customBodyRender: (value, tableMeta) => {
        const row = sortedVariants[tableMeta.rowIndex];
        const stock = localStock[row.id] ?? value ?? 0;
        const isUpdating = updatingVariant === row.id;

        return (
          <div className="quantity-box">
            <div className="qty-box d-flex align-items-center">
              <input
                type="number"
                value={stock}
                onChange={(e) => {
                  const newValue = e.target.value;
                  const quantity = Math.max(0, parseInt(newValue) || 0);
                  setLocalStock(prev => ({
                    ...prev,
                    [row.id]: quantity
                  }));
                  // Trigger API call on change with debounce
                  const timeoutId = setTimeout(() => {
                    handleQuantityUpdate(row.id, quantity);
                  }, 500);
                  return () => clearTimeout(timeoutId);
                }}
                min="0"
                className="form-control form-control-sm mx-2"
                style={{ textAlign: 'center', width: '100px' }}
                disabled={isUpdating}
              />
              {isUpdating && (
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">Updating...</span>
                </div>
              )}
            </div>
          </div>
        );
      },
    },
  };

  const columns = [
    ...commonColumns,
    ...(isSuitProduct
      ? suitInventoryTab === "coat"
        ? coatColumns
        : pantsColumns
      : [
          {
            name: "size_category",
            label: "Size Category",
            options: {
              customBodyRender: (value) => {
                return (
                  <div className="d-flex align-items-center justify-content-center gap-8">
                    {value || "-"}
                  </div>
                );
              },
            },
          },
          {
            name: "size_type",
            label: "Size Types",
            options: {
              customBodyRender: (value) => {
                return (
                  <div className="d-flex align-items-center justify-content-center gap-8">
                    {value || "-"}
                  </div>
                );
              },
            },
          },
          {
            name: "size_measurement",
            label: "Measurements",
            options: {
              customBodyRender: (value) => {
                return (
                  <div className="d-flex align-items-center justify-content-center gap-8">
                    {value || "-"}
                  </div>
                );
              },
            },
          },
        ]),
    stockColumn,
  ];

  const tableCount = isSuitProduct
    ? tableVariants.length
    : getPaginationTotal(variantsPagination, variants.length);

  const options = {
    selectableRows: "none",
    rowsPerPage,
    rowsPerPageOptions: getRowsPerPageOptions(
      tableCount
    ),
    responsive: "standard",
    elevation: 0,
    print: false,
    download: false,
    viewColumns: false,
    filter: false,
    search: true,
    sortOrder: getSortOrder(sortName, sortDirection),
    onColumnSortChange: (changedColumn, direction) => {
      setTableState({
        sortName: direction === "none" ? "" : changedColumn,
        sortDirection: direction === "none" ? "" : direction,
      });
    },
    serverSide: true,
    count: tableCount,
    page,
    onChangePage: (newPage) => {
      setTableState({ page: newPage });
    },
    onChangeRowsPerPage: (numberOfRows) => {
      setTableState({ rowsPerPage: numberOfRows, page: 0 });
    },
  };

  if (loading) {
    return (
      <div className="card basic-data-table">
        <div className="card-body text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading variants...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card basic-data-table">
        <div className="card-body text-center py-5">
          <div className="text-danger">
            <Icon icon="material-symbols:error-outline" width="48" height="48" />
            <p className="mt-3">Error: {typeof error === 'string' ? error : 'Failed to load variants'}</p>
            <button 
              className="btn btn-primary mt-3"
              onClick={() => dispatch(getProductVariants({ productId: id, page: page + 1, perPage: rowsPerPage }))}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="card basic-data-table">
        <div className="card-body">
          {isSuitProduct && (
            <div className="inventory-size-tabs size-tabs mb-3">
              <button
                type="button"
                className={suitInventoryTab === "coat" ? "active" : ""}
                onClick={() => {
                  setSuitInventoryTab("coat");
                  setTableState({ page: 0 });
                }}
              >
                Coat
              </button>
              <button
                type="button"
                className={suitInventoryTab === "pants" ? "active" : ""}
                onClick={() => {
                  setSuitInventoryTab("pants");
                  setTableState({ page: 0 });
                }}
              >
                Pants
              </button>
            </div>
          )}
          <MUIDataTable
            data={sortedVariants}
            columns={columns}
            options={options}
            className="overflow-hidden packageTable"
          />
        </div>
      </div>
    </>
  );
};

export default InnerInventoryList;
